import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { FerryDetailsModalData } from '@apps/main/new-desktop/modules/ferryDetailsModal';
import {
  useBookTripStore,
  useBrowserStore,
  useEditingIngredientStore,
  useItineraryStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { EditTripStep } from '@apps/main/new-desktop/modules/editTripModal';
import { EditTripStepType } from '@apps/main/new-desktop/modules/editTripModal';
import { EditTripModalOpenedLocationType } from '@apps/main/new-desktop/core/models';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { DestinationAutocompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import { useUserLocationStore } from '@apps/main/new-desktop/core/stores/userLocation';
import type { FlightDetailsModalData } from '@apps/main/new-desktop/modules/flightDetailsModal';
import type { FerryVehiclesRouteData } from '@apps/main/new-desktop/modules/ferryMultipleVehiclesModal';
import type { StayPriceBreakdownData } from '@apps/main/new-desktop/modules/priceBreakdownModal';
import type { RoomPriceBreakdownData } from '@apps/main/new-desktop/modules/priceBreakdownModal/models';
import { useEditTripStore } from '@apps/main/new-desktop/core/stores/editTrip/editTripStore';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useDestinationSelectorStore } from '@apps/main/new-desktop/core/stores/destinationSelectorStore';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import type {
  AutoCompleteSuggestion,
  IngredientId,
  PortInfo,
} from '@libs/trip-planner-interfaces';
import {
  isLocationSuggestion,
  TransportType,
} from '@libs/trip-planner-interfaces';
import { isStena } from '@libs/cookie';
import { LocalStorageService } from '@libs/utils';

import {
  ExternalErrorQueryParamValue,
  ModalQueryParamValue,
} from '../../../core/models/navigation';
import { ItinerarySegmentType, TripOverviewModalType } from '../models';
import type {
  ItineraryNoTransportSegment,
  PortSelectionUpdatePayload,
} from '../models';
import { useQueryParamsStore } from '../../../core/stores/queryParamsStore';

import { usePortSelectorStore } from './portSelector/portSelectorStore';
import { useTripOverviewPageStore } from './tripOverviewPageStore';
import { useTripOverviewFerryMultipleVehiclesStore } from './tripOverviewFerryMultipleVehiclesStore';
import { useTripOverviewPanelsStore } from './tripOverviewPanelsStore';

export const useTripOverviewModalsStore = defineStore(
  'TripOverviewModalsStore',
  () => {
    const tripOverviewFerryMultipleVehiclesStore =
      useTripOverviewFerryMultipleVehiclesStore();
    const portSelectorStore = usePortSelectorStore();
    const recipeStore = useRecipeStore();
    const queryParamsStore = useQueryParamsStore();
    const destinationSelectorStore = useDestinationSelectorStore();
    const tripOverviewPageStore = useTripOverviewPageStore();
    const editTripStore = useEditTripStore();
    const statsigEventsStore = useStatsigEventsStore();
    const userLocationStore = useUserLocationStore();
    const bookTripStore = useBookTripStore();
    const itineraryStore = useItineraryStore();
    const editingIngredientStore = useEditingIngredientStore();
    const tripOverviewPanelsStore = useTripOverviewPanelsStore();
    const browserStore = useBrowserStore();

    const localStorageService = new LocalStorageService();

    // State
    const isCookieModalOpen = ref(true);
    const tripOverviewModalType = ref<TripOverviewModalType>(
      TripOverviewModalType.None
    );
    const removeDestinationModalFor = ref<IngredientId | undefined>(undefined);
    const ferryDetailsModalData = ref<FerryDetailsModalData | undefined>();
    const flightDetailsModalData = ref<FlightDetailsModalData | undefined>();
    const tripDateRange = ref<string | undefined>(undefined);
    const tripNumberOfNights = ref<number | undefined>(undefined);
    const stayPriceBreakdown = ref<StayPriceBreakdownData | undefined>();
    const hasAlreadyOpenedInitialFerryModal = ref<boolean>(false);

    // Getters
    const isAnyModalOpen = computed<boolean>(
      () =>
        isCookieModalOpen.value ||
        tripOverviewModalType.value !== TripOverviewModalType.None
    );

    // Actions
    function openPriceBreakdownModal(ingredientId: IngredientId): void {
      if (!itineraryStore.selectedItinerary) {
        return;
      }

      const currentHotel =
        itineraryStore.selectedItinerary.hotelOnlyItinerary.find(
          (response) => response.ingredientId === ingredientId
        );

      if (!currentHotel) {
        return;
      }

      const currentStay =
        currentHotel.suggested ?? currentHotel.alternativeSuggestion;

      if (!currentStay) {
        return;
      }

      const roomsPriceBreakdown: RoomPriceBreakdownData[] =
        currentStay.rooms.map((room) => ({
          name: room.description,
          cancellationPolicies: room.cancellationPolicies,
          taxesAndFees: room.taxesAndFees,
          price: room.price,
        }));

      stayPriceBreakdown.value = {
        rooms: roomsPriceBreakdown,
        price: currentStay.totalPrice,
      };
      tripOverviewModalType.value = TripOverviewModalType.PriceBreakdown;
    }

    function openInitialFerryModalWhenPossible(): void {
      if (shouldOpenInitialFerryModal()) {
        openInitialFerryModal();
        hasAlreadyOpenedInitialFerryModal.value = true;
      }

      function shouldOpenInitialFerryModal(): boolean {
        const hotelOnlyLength = recipeStore.hotelOnlyRecipe.length;
        const recipeLength = recipeStore.recipe.length;
        const hasExactlyOneHotelIngredient =
          recipeLength === 1 && hotelOnlyLength === 1;

        return (
          !hasAlreadyOpenedInitialFerryModal.value &&
          hasExactlyOneHotelIngredient &&
          isStena()
        );
      }

      function openInitialFerryModal(): void {
        const ingredient = recipeStore.hotelOnlyRecipe[0];

        if (ingredient) {
          const fakeSegment: ItineraryNoTransportSegment = {
            segmentType: ItinerarySegmentType.NoTransport,
            ingredientId: ingredient.ingredientId,
            isReturnTransport: true,
            isLastSegment: false,
          };
          tripOverviewPageStore.selectTransport(
            fakeSegment,
            TransportType.Ferry
          );
        }
      }
    }

    function openExternalErrorModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.ExternalError;
    }

    function closeTripOverviewModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openDisneylandCampaignModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.DisneylandCampaign;
    }

    function closeDisneylandCampaignModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openFerryDetailsModal(modalData: FerryDetailsModalData): void {
      ferryDetailsModalData.value = modalData;
      tripOverviewModalType.value = TripOverviewModalType.FerryDetails;
    }

    function closeFerryDetailsModal(): void {
      ferryDetailsModalData.value = undefined;
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openWhyWeSuggestModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.WhyWeSuggest;
    }

    function openEditTripModal(
      initialStep: EditTripStep = { stepType: EditTripStepType.Overview }
    ): void {
      const editTripStepTypeToStatsigEvent: Record<
        EditTripStepType,
        EditTripModalOpenedLocationType
      > = {
        [EditTripStepType.AddDestination]:
          EditTripModalOpenedLocationType.TripOverviewAddDestinationButton,
        [EditTripStepType.UpdateDestination]:
          EditTripModalOpenedLocationType.TripOverviewDestinationSegment,
        [EditTripStepType.Overview]:
          EditTripModalOpenedLocationType.TripOverviewEditTripButton,
        [EditTripStepType.AddDestinationToOwnStay]:
          EditTripModalOpenedLocationType.TripOverviewAddDestinationToOwnStayButton,
      };
      statsigEventsStore.logTripOverviewEditTripModalOpened(
        editTripStepTypeToStatsigEvent[initialStep.stepType]
      );

      editTripStore.initialStep = { ...initialStep, isInitialStep: true };
      tripOverviewModalType.value = TripOverviewModalType.EditTrip;
    }

    function closeEditTripModal(): void {
      editTripStore.resetInitialStep();
      closeTripOverviewModal();
    }

    function openFerryMultipleVehiclesModal(ingredientId: IngredientId): void {
      tripOverviewFerryMultipleVehiclesStore.setData(ingredientId);
      tripOverviewModalType.value = TripOverviewModalType.FerryMultipleVehicles;
    }

    function closeFerryMultipleVehiclesModal(): void {
      tripOverviewFerryMultipleVehiclesStore.clearData();
      closeTripOverviewModal();
    }

    function confirmFerryMultipleVehiclesModal(
      data: FerryVehiclesRouteData[]
    ): void {
      tripOverviewFerryMultipleVehiclesStore.update(data);
      closeTripOverviewModal();
    }

    function openPremadeTripDetailsModal(): void {
      tripOverviewModalType.value =
        TripOverviewModalType.PremadeTripDescription;
    }

    function openPortSelectionModal(
      currentIngredientId: string | undefined,
      fromPort: PortInfo | undefined,
      toPort: PortInfo | undefined,
      currentTransportType: TransportType.Flight | TransportType.Ferry,
      multipleVehicles: boolean
    ): void {
      portSelectorStore.setModalData(
        currentIngredientId,
        fromPort,
        toPort,
        currentTransportType,
        multipleVehicles
      );
      tripOverviewModalType.value = TripOverviewModalType.PortSelector;
    }

    function closePortSelectionModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function confirmPortSelectionModal(
      payload: PortSelectionUpdatePayload
    ): void {
      portSelectorStore.setPortsForTransport(payload);
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    async function awaitForCookieConsent(): Promise<void> {
      const delay = 500;
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          const cookieConsentKey = 'osano_consentmanager';
          const hasVisitedCookieConsentModal =
            !!localStorageService.readRawItem(cookieConsentKey);
          if (hasVisitedCookieConsentModal) {
            clearInterval(interval);
            resolve();
          }
        }, delay);
      });
    }

    async function openModalAutomatically(): Promise<void> {
      // NOTE: This will be used in future A/B test
      const enableConfigurationPanelStepper = false;
      const modalParam = queryParamsStore.queryParams?.modal;
      const externalErrorParam = queryParamsStore.queryParams?.externalError;

      await awaitForCookieConsent();

      if (modalParam === ModalQueryParamValue.Pax) {
        if (
          enableConfigurationPanelStepper &&
          browserStore.isSmallerThan600px
        ) {
          // NOTE: This will be used in future A/B test
          // tripOverviewPanelsStore.openConfigurationStepperPanel();
        } else {
          tripOverviewPanelsStore.openConfigurationPanel(
            TabKey.Itinerary,
            'overview-header-actions'
          );
        }
      } else {
        openInitialFerryModalWhenPossible();
      }

      if (externalErrorParam === ExternalErrorQueryParamValue.BookingFailed) {
        statsigEventsStore.logExternalErrorDisplayed(
          ExternalErrorQueryParamValue.BookingFailed
        );
        openExternalErrorModal();
      }

      isCookieModalOpen.value = false;
    }

    function openPriceGuaranteeModal(): void {
      statsigEventsStore.logTripOverviewPriceGuaranteeModalOpened();
      tripOverviewModalType.value = TripOverviewModalType.PriceGuarantee;
    }

    function openDestinationSelectorModal(
      searchText: string,
      ingredientId: IngredientId | undefined,
      modalType: DestinationAutocompleteType
    ): void {
      destinationSelectorStore.setDestinationSelectorData(
        searchText,
        ingredientId,
        modalType
      );
      tripOverviewModalType.value = TripOverviewModalType.DestinationSelector;
    }

    function selectUserPosition(
      autocompleteType: DestinationAutocompleteType,
      placeId?: string
    ): void {
      if (autocompleteType === AutoCompleteType.StartingPosition) {
        userLocationStore.setUserStartLocation(placeId);
      }

      if (autocompleteType === AutoCompleteType.EndingPosition) {
        userLocationStore.setUserEndLocation(placeId);
      }
      destinationSelectorStore.clearDestinationSelectorData();
      closeTripOverviewModal();
    }

    function confirmDestinationSelectorModal(
      newDestination: AutoCompleteSuggestion | null | undefined
    ): void {
      if (newDestination) {
        if (
          isLocationSuggestion(newDestination) &&
          (destinationSelectorStore.autocompleteType ===
            AutoCompleteType.StartingPosition ||
            destinationSelectorStore.autocompleteType ===
              AutoCompleteType.EndingPosition)
        ) {
          selectUserPosition(
            destinationSelectorStore.autocompleteType,
            newDestination.placeId
          );
        } else {
          destinationSelectorStore.handleConfirmSelection(newDestination);
        }
      }
      closeTripOverviewModal();
    }

    function openRemoveDestinationModal(ingredientId: IngredientId): void {
      removeDestinationModalFor.value = ingredientId;
      tripOverviewModalType.value = TripOverviewModalType.RemoveDestination;
    }

    function confirmRemoveDestinaionModal(): void {
      if (!removeDestinationModalFor.value) {
        return;
      }
      statsigEventsStore.logTripOverviewRemoveDestination();
      recipeStore.deleteDestinationIngredient(removeDestinationModalFor.value);
      removeDestinationModalFor.value = undefined;
      closeTripOverviewModal();
    }

    function buildNewTrip(): void {
      recipeStore.updateRecipe([]);
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openAddFlightsModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.AddFlights;
    }

    function closeAddFlightsModal(): void {
      editingIngredientStore.doneEditingIngredient();
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openFlightDetailsModal(modalData: FlightDetailsModalData): void {
      flightDetailsModalData.value = modalData;
      tripOverviewModalType.value = TripOverviewModalType.FlightDetails;
    }

    function closeFlightDetailsModal(): void {
      flightDetailsModalData.value = undefined;
      tripOverviewModalType.value = TripOverviewModalType.None;
    }

    function openContinueWithSuggestedDateModal(
      dateRange: string,
      numberOfNights: number | undefined
    ): void {
      tripDateRange.value = dateRange;
      tripNumberOfNights.value = numberOfNights;
      tripOverviewModalType.value =
        TripOverviewModalType.ContinueWithSuggestedDate;
    }

    function confirmContinueWithSuggestedDateModal(): void {
      tripDateRange.value = undefined;
      tripNumberOfNights.value = undefined;
      tripOverviewModalType.value = TripOverviewModalType.None;
      bookTripStore.navigateToCheckout();
    }

    function closeContinueWithSuggestedDateModal(): void {
      tripDateRange.value = undefined;
      tripNumberOfNights.value = undefined;
      tripOverviewModalType.value = TripOverviewModalType.None;
      tripOverviewPageStore.scrollToCalendar();
    }

    function closePriceBreakdownModal(): void {
      tripOverviewModalType.value = TripOverviewModalType.None;
      stayPriceBreakdown.value = undefined;
    }

    return {
      // State
      tripOverviewModalType,
      ferryDetailsModalData,
      flightDetailsModalData,
      tripDateRange,
      tripNumberOfNights,

      // Getters
      isAnyModalOpen,
      stayPriceBreakdown,

      // Actions
      openEditTripModal,
      closeEditTripModal,
      closeTripOverviewModal,
      openFerryDetailsModal,
      closeFerryDetailsModal,
      openPremadeTripDetailsModal,
      openWhyWeSuggestModal,
      openPortSelectionModal,
      closePortSelectionModal,
      openFerryMultipleVehiclesModal,
      closeFerryMultipleVehiclesModal,
      confirmFerryMultipleVehiclesModal,
      confirmPortSelectionModal,
      openModalAutomatically,
      openPriceGuaranteeModal,
      openDestinationSelectorModal,
      selectUserPosition,
      confirmDestinationSelectorModal,
      openRemoveDestinationModal,
      confirmRemoveDestinaionModal,
      openAddFlightsModal,
      closeAddFlightsModal,
      openFlightDetailsModal,
      closeFlightDetailsModal,
      openContinueWithSuggestedDateModal,
      confirmContinueWithSuggestedDateModal,
      closeContinueWithSuggestedDateModal,
      openPriceBreakdownModal,
      closePriceBreakdownModal,
      buildNewTrip,
      openInitialFerryModalWhenPossible,
      openDisneylandCampaignModal,
      closeDisneylandCampaignModal,
    };
  }
);
